import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { Select, message } from 'antd';
import type { SelectProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { INTL_ACTION, INTL_ENTITY, getIntlTranslation } from '@/utils/intl';
import { useHasAccess } from '@/utils/hooks';
import { getAPIErrorMessage, processAPIResponseNow } from '@/utils/request';
import { createSkillRequest } from '@/services/api/skill';
import * as SkillSelectors from '@/selectors/skillSelectors';
import type { SkillModel } from '@/typings/models/Skill';
import { SKILLS_ACCESS } from './constants';
import { skillOptions, sortSlugsByLabel } from './utils';
import ManageSkillsFooter from './ManageSkillsFooter';
import styles from './index.module.less';

/** Option value of the "Create skill …" row; never a real slug (slugs have no spaces). */
const CREATE_VALUE = '__create skill__';

type SkillSelectProps = Pick<SelectProps, 'disabled' | 'placeholder' | 'autoFocus' | 'className' | 'getPopupContainer'> & {
  /** Selected skill slugs (what the API takes), kept in alphabetical order of their labels. */
  value?: string[];
  onChange?: (slugs: string[]) => void;
};

/**
 * Picks skills to attach. With skills_manage, typing a label that doesn't exist
 * yet offers to create it on the spot, and a footer link opens the skills drawer.
 * Works as a Form.Item child (value/onChange).
 */
const SkillSelect: React.FC<SkillSelectProps> = ({ value, onChange, placeholder, ...selectProps }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const skills = useSelector(SkillSelectors.selectSkills);
  const initialized = useSelector(SkillSelectors.selectSkillsInitialized);
  const canManage = useHasAccess(SKILLS_ACCESS.manage);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const selected = useMemo(() => sortSlugsByLabel(value || [], skills), [value, skills]);

  useEffect(() => {
    if (!initialized) dispatch({ type: 'skills/fetch' });
  }, [initialized]);

  const term = search.trim();
  const exists = skills.some((skill) => skill.label.toLowerCase() === term.toLowerCase());
  const options = useMemo(() => {
    const list: SelectProps['options'] = skillOptions(skills);
    if (canManage && term && !exists) {
      list.push({
        value: CREATE_VALUE,
        label: (
          <span className={styles.createOption}>
            <PlusOutlined />
            {getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.CREATE })} “{term}”
          </span>
        ),
        // Matched against the search like a real option, so it isn't filtered out.
        searchLabel: term,
      });
    }
    return list;
  }, [intl, skills, canManage, term, exists]);

  const emit = useCallback(
    (slugs: string[], list: Pick<SkillModel, 'label' | 'slug'>[] = skills) => onChange?.(sortSlugsByLabel(slugs, list)),
    [onChange, skills],
  );

  const createSkill = useCallback(
    async (label: string, slugs: string[]) => {
      setCreating(true);
      const response = await createSkillRequest({ label });
      processAPIResponseNow(
        response,
        {
          successMessage: getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.CREATE_SUCCESS }),
          onEnd: () => setCreating(false),
          onSuccess: (skill: SkillModel) => {
            setSearch('');
            emit([...slugs, skill.slug], [...skills, skill]);
            dispatch({ type: 'skills/fetch' });
          },
          onError: (error) => message.error(getAPIErrorMessage(error)),
        },
        'data',
      );
    },
    [intl, emit, skills, dispatch],
  );

  const handleChange = useCallback(
    (slugs: string[]) => {
      const realSlugs = slugs.filter((slug) => slug !== CREATE_VALUE);
      if (realSlugs.length !== slugs.length) {
        createSkill(term, realSlugs);
        return;
      }
      setSearch('');
      emit(realSlugs);
    },
    [createSkill, term, emit],
  );

  const openSkillsDrawer = useCallback(() => {
    // Otherwise the popup stays open on top of the skills drawer.
    setOpen(false);
    dispatch({ type: 'skills/openDrawer' });
  }, [dispatch]);

  const popupRender = useCallback(
    (menu: React.ReactElement) =>
      canManage ? (
        <>
          {menu}
          <ManageSkillsFooter onClick={openSkillsDrawer} />
        </>
      ) : (
        menu
      ),
    [canManage, openSkillsDrawer],
  );

  return (
    <Select
      {...selectProps}
      mode="multiple"
      value={selected}
      onChange={handleChange}
      options={options}
      loading={!initialized || creating}
      showSearch={{
        searchValue: search,
        onSearch: setSearch,
        optionFilterProp: ['label', 'searchLabel'],
      }}
      open={open}
      onOpenChange={setOpen}
      popupRender={popupRender}
      popupMatchSelectWidth={false}
      placeholder={
        placeholder ?? getIntlTranslation(intl, { entity: INTL_ENTITY.SKILL, action: INTL_ACTION.SELECT_PLURAL })
      }
    />
  );
};

export default React.memo(SkillSelect);
